import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { assignmentEmailsAPI } from '../../services/api';
import { 
  Mail, 
  AlertCircle, 
  RefreshCw, 
  Trash2, 
  Clock, 
  XCircle,
  AlertTriangle,
  Wifi,
  Key,
  CheckCircle
} from 'lucide-react';

const FailedEmailsTab = () => {
  const [failedEmails, setFailedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [retrying, setRetrying] = useState(null);

  const fetchFailedEmails = async () => {
    try {
      setLoading(true);
      const response = await assignmentEmailsAPI.getFailedEmails(100);
      setFailedEmails(response.data.failed_emails || []);
    } catch (error) {
      console.error('Failed to fetch failed emails:', error);
      toast.error('Failed to load failed emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFailedEmails();
  }, []);

  const handleRetry = async (emailId) => {
    setRetrying(emailId);
    try {
      await assignmentEmailsAPI.retryFailedEmail(emailId);
      toast.success('Email marked for retry');
      fetchFailedEmails();
    } catch (error) {
      toast.error('Failed to retry email');
    } finally {
      setRetrying(null);
    }
  };

  const handleDismiss = async (emailId) => {
    try {
      await assignmentEmailsAPI.dismissFailedEmail(emailId);
      toast.success('Failed email dismissed');
      setFailedEmails(prev => prev.filter(e => e.id !== emailId));
    } catch (error) {
      toast.error('Failed to dismiss email');
    }
  };

  const handleDismissAll = async () => {
    if (!window.confirm('Are you sure you want to dismiss all failed emails?')) return;
    
    try {
      for (const email of filteredEmails) {
        await assignmentEmailsAPI.dismissFailedEmail(email.id);
      }
      toast.success(`Dismissed ${filteredEmails.length} failed emails`);
      fetchFailedEmails();
    } catch (error) {
      toast.error('Failed to dismiss all emails');
    }
  };

  const getErrorIcon = (errorCode) => {
    switch (errorCode) {
      case 'RATE_LIMIT':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'AUTH_ERROR':
        return <Key className="h-4 w-4 text-[var(--error)]" />;
      case 'INVALID_EMAIL':
        return <XCircle className="h-4 w-4 text-orange-500" />;
      case 'CONNECTION_ERROR':
        return <Wifi className="h-4 w-4 text-[#D4AF37]" />;
      case 'DOMAIN_ERROR':
        return <AlertTriangle className="h-4 w-4 text-[#F4C2C2]" />;
      default:
        return <AlertCircle className="h-4 w-4 text-[var(--text-muted)]" />;
    }
  };

  const getErrorBadgeColor = (errorCode) => {
    switch (errorCode) {
      case 'RATE_LIMIT':
        return 'bg-amber-100 text-amber-800 border-[var(--warning)]/30';
      case 'AUTH_ERROR':
        return 'bg-[var(--error)]/15 text-[var(--error)] border-[var(--error)]/30';
      case 'INVALID_EMAIL':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'CONNECTION_ERROR':
        return 'bg-[#D4AF37]/10 text-blue-800 border-[#D4AF37]/30';
      case 'DOMAIN_ERROR':
        return 'bg-[#F4C2C2]/15 text-purple-800 border-[#F4C2C2]/30';
      default:
        return 'bg-[var(--background-subtle)] text-[var(--text-primary)] border-[var(--border-primary)]';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'pending_retry') {
      return <Badge className="bg-[#D4AF37]/10 text-blue-800">Pending Retry</Badge>;
    }
    return <Badge className="bg-[var(--error)]/15 text-[var(--error)]">Failed</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEmails = failedEmails.filter(email => {
    if (filter === 'all') return true;
    return email.error_code === filter;
  });

  const errorCounts = failedEmails.reduce((acc, email) => {
    const code = email.error_code || 'UNKNOWN_ERROR';
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="failed-emails-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-100">
          <CardContent className="p-4 text-center">
            <Mail className="h-8 w-8 mx-auto text-[var(--error)] mb-2" />
            <p className="text-2xl font-bold text-[var(--error)]">{failedEmails.length}</p>
            <p className="text-sm text-[var(--error)]/70">Total Failed</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100">
          <CardContent className="p-4 text-center">
            <Clock className="h-8 w-8 mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-amber-600">{errorCounts['RATE_LIMIT'] || 0}</p>
            <p className="text-sm text-amber-600/70">Rate Limited</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-100">
          <CardContent className="p-4 text-center">
            <XCircle className="h-8 w-8 mx-auto text-orange-500 mb-2" />
            <p className="text-2xl font-bold text-orange-600">{errorCounts['INVALID_EMAIL'] || 0}</p>
            <p className="text-sm text-orange-600/70">Invalid Email</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
          <CardContent className="p-4 text-center">
            <Wifi className="h-8 w-8 mx-auto text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#D4AF37]">{errorCounts['CONNECTION_ERROR'] || 0}</p>
            <p className="text-sm text-[#D4AF37]/70">Connection Errors</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Actions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-[var(--error)]" />
              Failed Emails
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-select">
                  <SelectValue placeholder="Filter by error" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Errors</SelectItem>
                  <SelectItem value="RATE_LIMIT">Rate Limited</SelectItem>
                  <SelectItem value="AUTH_ERROR">Auth Error</SelectItem>
                  <SelectItem value="INVALID_EMAIL">Invalid Email</SelectItem>
                  <SelectItem value="CONNECTION_ERROR">Connection Error</SelectItem>
                  <SelectItem value="DOMAIN_ERROR">Domain Error</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchFailedEmails}
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              
              {filteredEmails.length > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleDismissAll}
                  data-testid="dismiss-all-btn"
                >
                  Dismiss All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEmails.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 mx-auto text-green-400 mb-4" />
              <h3 className="text-lg font-medium text-[var(--text-primary)]">No Failed Emails</h3>
              <p className="text-[var(--text-muted)] mt-1">
                {filter === 'all' 
                  ? 'All emails have been sent successfully!' 
                  : `No emails with ${filter.replace('_', ' ').toLowerCase()} error`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEmails.map((email) => (
                <div 
                  key={email.id} 
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-[var(--background-subtle)] rounded-lg border hover:bg-[var(--background-subtle)] transition-colors gap-3"
                  data-testid={`failed-email-${email.id}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 mt-1">
                      {getErrorIcon(email.error_code)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[var(--text-primary)] truncate">
                          {email.to_email}
                        </span>
                        {getStatusBadge(email.status)}
                      </div>
                      <p className="text-sm text-[var(--text-secondary)] truncate mt-1" title={email.subject}>
                        {email.subject}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className={getErrorBadgeColor(email.error_code)}>
                          {email.error_code?.replace('_', ' ') || 'Unknown'}
                        </Badge>
                        <span className="text-xs text-[var(--text-muted)]">
                          {formatDate(email.created_at)}
                        </span>
                        {email.retry_count > 0 && (
                          <span className="text-xs text-[#D4AF37]">
                            Retried {email.retry_count}x
                          </span>
                        )}
                      </div>
                      {email.error_message && (
                        <p className="text-xs text-[var(--text-muted)] mt-1 truncate" title={email.error_message}>
                          {email.error_message.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRetry(email.id)}
                      disabled={retrying === email.id || email.status === 'pending_retry'}
                      data-testid={`retry-btn-${email.id}`}
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${retrying === email.id ? 'animate-spin' : ''}`} />
                      Retry
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDismiss(email.id)}
                      className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                      data-testid={`dismiss-btn-${email.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-900 mb-2">Understanding Error Types</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-amber-500 mt-0.5" />
              <div>
                <span className="font-medium text-amber-700">Rate Limited:</span>
                <span className="text-[var(--text-secondary)] ml-1">Email service quota exceeded. Retry later.</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Key className="h-4 w-4 text-[var(--error)] mt-0.5" />
              <div>
                <span className="font-medium text-red-700">Auth Error:</span>
                <span className="text-[var(--text-secondary)] ml-1">API key issue. Contact support.</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <XCircle className="h-4 w-4 text-orange-500 mt-0.5" />
              <div>
                <span className="font-medium text-orange-700">Invalid Email:</span>
                <span className="text-[var(--text-secondary)] ml-1">Recipient email is invalid.</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Wifi className="h-4 w-4 text-[#D4AF37] mt-0.5" />
              <div>
                <span className="font-medium text-[#D4AF37]">Connection Error:</span>
                <span className="text-[var(--text-secondary)] ml-1">Network issue. Retry later.</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FailedEmailsTab;
